import { Skeleton } from '@/design-system';
import type { PublicDocument } from '@/entities/publicReport';

import { copyFor } from '../copy';
import type { UsePublicReportResult } from '../usePublicReport';
import { AccessCodeGate } from './AccessCodeGate';
import { PublicProblem } from './PublicProblem';
import { PublicReportBlocks } from './PublicReportBlocks';

export interface PublicReportContentProps {
    state: UsePublicReportResult;
    /** `1` op de publieke pagina; `2` in de admin-preview. */
    headingLevel: 1 | 2;
    /**
     * Alleen in de admin-preview: de downloadknop haalt de PDF met de sessie op.
     * Op de publieke pagina haalt `state.openDocument` hem met de link op.
     */
    onOpenDocument?: (document: PublicDocument) => boolean | void;
    /** Alleen in de admin-preview: vragen en doorsturen versturen niets, en deze regel zegt dat. */
    previewNote?: string;
    /** Blijven de tabbladen bovenaan plakken? In het kader van kantoor niet. */
    pinned?: boolean;
}

/**
 * Wat er op de witte pagina staat: het codeveld, een foutpagina, de laadstand
 * of het rapport. Koper en kantoor zien hier precies hetzelfde.
 */
export function PublicReportContent({
    state,
    headingLevel,
    onOpenDocument,
    previewNote,
    pinned = true,
}: PublicReportContentProps) {
    const copy = copyFor(state.language);
    const kind = state.problem?.kind ?? null;

    if (kind === 'codeRequired' || kind === 'codeInvalid' || state.checkingCode) {
        return (
            <AccessCodeGate
                tenant={state.tenant}
                copy={copy}
                invalid={kind === 'codeInvalid'}
                attemptsLeft={state.problem?.attemptsLeft ?? null}
                busy={state.checkingCode}
                headingLevel={headingLevel}
                onSubmit={state.submitAccessCode}
            />
        );
    }

    if (state.problem) {
        return (
            <PublicProblem
                problem={state.problem}
                tenant={state.tenant}
                copy={copy}
                language={state.language}
                headingLevel={headingLevel}
                onRetry={state.retry}
            />
        );
    }

    if (!state.report) {
        return (
            <div className="flex flex-col gap-3 px-[var(--pr-pad-x)] py-9">
                <Skeleton width="45%" height={14} label={copy.loading} />
                <Skeleton width="80%" height={26} radius={8} />
                <Skeleton width="65%" height={14} />
                <Skeleton height={48} radius={8} />
            </div>
        );
    }

    const openDocument = onOpenDocument ?? state.openDocument;

    return (
        <PublicReportBlocks
            report={state.report}
            language={state.language}
            onLanguageChange={state.setLanguage}
            onShare={state.share}
            onAsk={state.ask}
            onLinkExpired={state.refreshLinks}
            heroLevel={headingLevel}
            {...(openDocument ? { onOpenDocument: openDocument } : {})}
            {...(state.documentState ? { documentState: state.documentState } : {})}
            {...(previewNote ? { previewNote } : {})}
            pinned={pinned}
            translationState={state.translationState ?? null}
        />
    );
}
